import React from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import useUrgentRiskAlerts from '../../hooks/useUrgentRiskAlerts';
import { overviewAlertLines } from '../../utils/urgentRiskAlerts';

/*
 * UrgentRiskAlertFlag (TDI-701)
 *
 * The patient overview's flag that an urgent risk alert went to the service,
 * one entry per alert: when, to which service, after whose answer, the
 * question and answer as the person saw them, the answer now if it changed,
 * and whether the form is still unsubmitted. Every provider role that opens
 * the record sees it, at any stage.
 *
 * Factual only: no rating, no prompt to act, nothing filled in on the
 * decision. Amber, icon and text, as the second-review banner and the
 * consent-withdrawn banner. No design frame exists for the BTH side; the
 * wording is the ticket's and waits on the safeguarding wording check.
 *
 * Shows nothing while loading and nothing when no alert was sent. If the
 * alerts cannot be loaded it says so, so a clinician is never left thinking
 * there is no alert when the check failed.
 */
export default function UrgentRiskAlertFlag({ orgId, patientRecordId }) {
  const { alerts, loading, error } = useUrgentRiskAlerts(orgId, patientRecordId);

  if (loading) return null;
  if (error) {
    return (
      <div
        role="status"
        className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left flex items-start gap-3"
      >
        <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
        <p className="text-sm text-amber-900">
          We couldn&rsquo;t check for urgent risk alerts. Refresh the page to try again.
        </p>
      </div>
    );
  }
  if (!alerts.length) return null;

  return (
    <section
      aria-labelledby="urgent-risk-alert-flag-title"
      className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left flex items-start gap-3"
    >
      <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
      <div className="min-w-0 text-sm text-amber-900">
        <h2 id="urgent-risk-alert-flag-title" className="font-semibold">
          Urgent risk alert sent
        </h2>
        <ul className="mt-1 space-y-2">
          {alerts.map((alert) => (
            <li key={alert.id || `${alert.questionId}-${alert.sentAt}`}>
              {overviewAlertLines(alert).map((line) => (
                <p key={line}>{line}</p>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
